/**
 * PlaceOfPerformanceV2 - Shared Place of Performance Display Component
 *
 * Pure, idempotent presentation component for displaying Place of Performance data.
 * Can be used standalone or composed with SidebarWidgetV2 for sidebar display.
 *
 * Features:
 * - Pre-computed display values (no computation in JSX)
 * - Handles both domestic and foreign locations
 * - Shows county, ZIP+4, congressional district with redistricting history
 * - Supports full and compact display variants
 *
 * @example
 * ```tsx
 * // Full display (used in sidebar widgets)
 * <PlaceOfPerformanceV2 data={normalizedPop} />
 *
 * // Compact display (used in popovers)
 * <PlaceOfPerformanceV2 data={normalizedPop} variant="compact" />
 *
 * // With custom class
 * <PlaceOfPerformanceV2 data={normalizedPop} className="mt-2" />
 * ```
 */
import React from 'react'

import { twMerge } from 'tailwind-merge'

import type { NormalizedPlaceOfPerformance } from '../transforms'

// ============================================================================
// Types
// ============================================================================

interface PlaceOfPerformanceV2Props {
  /** Normalized PoP data from normalizePlaceOfPerformance() */
  data: NormalizedPlaceOfPerformance
  /** Display variant: 'full' for sidebar widgets, 'compact' for popovers */
  variant?: 'full' | 'compact'
  /** Additional CSS classes */
  className?: string
}

// ============================================================================
// Sub-Components (Pure Presentation)
// ============================================================================

interface DetailRowProps {
  label: string
  value: string | null
  className?: string
}

/**
 * DetailRow - Single detail row for PoP display
 * Pure presentation component
 */
const DetailRow: React.FC<DetailRowProps> = ({ label, value, className }) => {
  if (!value) return null
  return (
    <div
      className={twMerge(
        'text-xs text-primary-500 dark:text-primary-400',
        className,
      )}
    >
      {label}: {value}
    </div>
  )
}

interface CongressionalDistrictProps {
  formatted: string | null
  original: string | null
  hasRedistricted: boolean
  className?: string
}

/**
 * CongressionalDistrict - Congressional district display with redistricting note
 * Pure presentation component
 */
const CongressionalDistrict: React.FC<CongressionalDistrictProps> = ({
  formatted,
  original,
  hasRedistricted,
  className,
}) => {
  if (!formatted) return null
  return (
    <div
      className={twMerge(
        'text-xs text-primary-500 dark:text-primary-400',
        className,
      )}
    >
      Congressional District: {formatted}
      {hasRedistricted && original && (
        <span className="ml-1 text-primary-400 dark:text-primary-500">
          (was {original})
        </span>
      )}
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * PlaceOfPerformanceV2 - Pure presentation component for PoP display
 *
 * Accepts pre-normalized data and renders without any computation.
 * Same input always produces same output (idempotent).
 */
export const PlaceOfPerformanceV2: React.FC<PlaceOfPerformanceV2Props> = ({
  data,
  variant = 'full',
  className,
}) => {
  // Don't render if no formatted location
  if (!data.formatted) return null

  const isCompact = variant === 'compact'

  return (
    <div className={twMerge('space-y-1', className)}>
      {/* Primary location */}
      <div
        className={twMerge(
          'font-medium text-primary-900 dark:text-primary-100',
          isCompact ? 'text-xs' : 'text-[13px]',
        )}
      >
        {data.formatted}
      </div>

      {/* County (domestic locations only) */}
      {data.county && !data.isForeign && (
        <div
          className={twMerge(
            'text-primary-500 dark:text-primary-400',
            isCompact ? 'text-xs' : 'text-xs',
          )}
        >
          {data.county} County
        </div>
      )}

      {/* Foreign location details */}
      {data.isForeign && (
        <div className={twMerge('space-y-0.5', isCompact ? '' : 'mt-1')}>
          {data.foreignProvince && (
            <div
              className={twMerge(
                'text-primary-500 dark:text-primary-400',
                isCompact ? 'text-xs' : 'text-xs',
              )}
            >
              Province: {data.foreignProvince}
            </div>
          )}
          {data.foreignDescription && (
            <div
              className={twMerge(
                'text-primary-500 dark:text-primary-400',
                isCompact ? 'text-xs' : 'text-xs',
              )}
            >
              {data.foreignDescription}
            </div>
          )}
        </div>
      )}

      {/* ZIP code (pre-formatted with +4 extension) */}
      {data.zipFormatted && (
        <div
          className={twMerge(
            'text-primary-500 dark:text-primary-400',
            isCompact ? 'text-xs' : 'text-xs',
            !isCompact && 'mt-1',
          )}
        >
          ZIP: {data.zipFormatted}
        </div>
      )}

      {/* Congressional district with redistricting note */}
      <CongressionalDistrict
        formatted={data.congressionalDistrictFormatted}
        original={data.congressionalDistrict}
        hasRedistricted={data.hasRedistricted}
        className={isCompact ? 'text-xs' : undefined}
      />
    </div>
  )
}

PlaceOfPerformanceV2.displayName = 'PlaceOfPerformanceV2'

// ============================================================================
// Exports
// ============================================================================

export type { PlaceOfPerformanceV2Props }
